import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { v2Api } from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';
import {
  getKybDetailsAsync,
  IGetKybDetailsResponse,
} from '../kyb/getKybDetailsSlice';

interface IGetDocumentRequestV2Response {
  status?: boolean;
  message?: string;
  data?: {
    id?: string;
    businessId?: string;
    kybId?: string;
    userId?: string;
    names?: {
      name: string;
      templateUrl: string;
    }[];
    submitted?: boolean;
    submittedAt?: string;
    createdAt?: string;
    updatedAt?: string;
    reason?: string;
  };
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetDocumentRequestV2Response> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getDocumentRequestV2Async = createAsyncThunk(
  'kyb/documentRequest',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId = localStorage.getItem('businessId');

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      let data = null;

      if (businessId) {
        const kybDetails = await dispatch(getKybDetailsAsync());

        if (kybDetails.meta.requestStatus === 'fulfilled') {
          const kybDetailsPayload =
            kybDetails.payload as IGetKybDetailsResponse;

          const kybId = kybDetailsPayload.data?.id ?? '';

          const response = await v2Api.get(
            `kyb/${kybId}/document-request`,
            config,
          );

          data = response.data;
        }
      }

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;

      return rejectWithValue(error.response?.data);
    }
  },
);

export const documentRequestGetV2Slice = createSlice({
  name: 'kyb/documentRequest/v2/get',
  initialState,
  reducers: {
    resetGetDocumentRequestV2: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDocumentRequestV2Async.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getDocumentRequestV2Async.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetDocumentRequestV2Response;
      })
      .addCase(getDocumentRequestV2Async.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IGetDocumentRequestV2Response;
        state.error = action.error.message;
      });
  },
});

export const { resetGetDocumentRequestV2 } = documentRequestGetV2Slice.actions;

export default documentRequestGetV2Slice.reducer;
